// apps/web/src/features/kronos/timer/TimerStopDialog.tsx
import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useActiveTags } from '../useKronosData';

interface TimerStopDialogProps {
  open: boolean;
  projectLabel: string;
  designationLabel: string;
  elapsedFormatted: string;
  initialDescription?: string;
  initialTags?: string[];
  onSave: (data: { description: string; tags: string[]; billable: boolean }) => void;
  onDiscard: () => void;
  onKeepTiming: () => void;
}

export default function TimerStopDialog({
  open,
  projectLabel,
  designationLabel,
  elapsedFormatted,
  initialDescription = '',
  initialTags,
  onSave,
  onDiscard,
  onKeepTiming,
}: TimerStopDialogProps) {
  const { t } = useTranslation();
  const tagOptions = useActiveTags();
  const [description, setDescription] = useState(initialDescription);
  const [tags, setTags] = useState<string[]>(initialTags ?? []);
  const [billable, setBillable] = useState(true);
  const [saving, setSaving] = useState(false);

  // Sync description + tags when the dialog opens with new initial values
  // (tags carried over from the timer-start form).
  const initialTagsKey = (initialTags ?? []).join('');
  useEffect(() => {
    if (open) {
      setDescription(initialDescription);
      setTags(initialTags ?? []);
    }
    // initialTagsKey collapses the array dep to a stable string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDescription, initialTagsKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ description, tags, billable });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onKeepTiming} maxWidth="sm" fullWidth>
      <DialogTitle>{t('kronos.timer.stop.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {projectLabel} — {designationLabel}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
            {elapsedFormatted}
          </Typography>
        </Box>

        <TextField
          label={t('kronos.timer.stop.addDescription')}
          multiline
          maxRows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
          autoFocus
        />

        <Autocomplete
          multiple
          options={[...new Set([...tagOptions, ...tags])]}
          value={tags}
          onChange={(_, v) => setTags(v)}
          renderInput={(params) => (
            <TextField {...params} label={t('kronos.tracker.tags')} placeholder={t('kronos.tracker.tagsPlaceholder')} />
          )}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={<Switch checked={billable} onChange={(e) => setBillable(e.target.checked)} />}
          label={t('kronos.tracker.billable')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDiscard} color="secondary" variant="outlined" disabled={saving}>
          {t('kronos.timer.stop.discard')}
        </Button>
        <Button onClick={onKeepTiming} disabled={saving}>
          {t('kronos.timer.stop.keepTiming')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {t('kronos.timer.stop.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
